import type { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";

import { useCftSubpageConfig } from "scmp-app/components/plus/cft/content-list/hooks";

import { PlusCftIndustryFilterButton } from "./filter-button";
import { Container } from "./styles";

type Props = {
  industryTopics: Record<string, string[]>;
  selectedIndustryNames: string[];
  setIndustryTopics: Dispatch<SetStateAction<Record<string, string[]>>>;
  setSelectedIndustryNames: Dispatch<SetStateAction<string[]>>;
};

export const PlusCftIndustryFilterTabs: FunctionComponent<Props> = ({
  industryTopics,
  selectedIndustryNames,
  setIndustryTopics,
  setSelectedIndustryNames,
}) => {
  const subpageConfig = useCftSubpageConfig();
  const [loadCounter, setLoadCounter] = useState(subpageConfig.nodequeueItems.length);

  const isAllSelected = selectedIndustryNames.length === Object.keys(industryTopics).length;

  const handleIndustryTopics = useCallback(
    (industryName: string, topicIds: string[]) => {
      setIndustryTopics(state => ({
        ...state,
        [industryName]: topicIds,
      }));
      setLoadCounter(count => count - 1);
    },
    [setIndustryTopics],
  );

  const handleSelectIndustry = useCallback(
    (industryName: string) => {
      setSelectedIndustryNames(state => {
        if (isAllSelected) return [industryName];
        if (state.includes(industryName)) {
          const result = state.filter(name => name !== industryName);
          if (result.length === 0) {
            return Object.keys(industryTopics);
          }
          return result;
        }
        return [...state, industryName];
      });
    },
    [industryTopics, isAllSelected, setSelectedIndustryNames],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedIndustryNames(() => Object.keys(industryTopics));
  }, [industryTopics, setSelectedIndustryNames]);

  useEffect(() => {
    if (loadCounter === 0) {
      handleSelectAll();
    }
  }, [handleSelectAll, loadCounter]);

  return (
    <Container>
      <PlusCftIndustryFilterButton isActive={isAllSelected} name="ALL" onClick={handleSelectAll} />

      {subpageConfig.nodequeueItems.map(industry => {
        const isSelected = !isAllSelected && selectedIndustryNames.includes(industry.name);
        return (
          <ClientSideSuspense key={industry.name}>
            <PlusCftIndustryFilterButton
              isActive={isSelected}
              name={industry.name}
              onClick={handleSelectIndustry}
              onLoad={handleIndustryTopics}
              queue={industry.queueName}
            />
          </ClientSideSuspense>
        );
      })}
    </Container>
  );
};

PlusCftIndustryFilterTabs.displayName = "PlusCftIndustryFilterTabs";
